import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pr_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [discount, setDiscount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pr_discount") || "null");
    } catch {
      return null;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("pr_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("pr_discount", JSON.stringify(discount));
  }, [discount]);

  const addItem = (product, size, qty = 1, color = null) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.id === product.id && i.size === size && (i.color || null) === color
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          size,
          color,
          qty,
        },
      ];
    });
    toast.success(`${product.name} (${[color, size].filter(Boolean).join(" / ")}) added to cart`);
  };

  const removeItem = (id, size, color = null) =>
    setItems((prev) => prev.filter((i) => !(i.id === id && i.size === size && (i.color || null) === color)));

  const setQty = (id, size, color, qty) => {
    if (qty < 1) return removeItem(id, size, color);
    setItems((prev) =>
      prev.map((i) =>
        i.id === id && i.size === size && (i.color || null) === (color || null) ? { ...i, qty } : i
      )
    );
  };

  const applyDiscount = async (code) => {
    try {
      const { data } = await api.post("/discounts/validate", { code, subtotal });
      setDiscount({ code: data.code, type: data.type, value: data.value });
      toast.success(`Code ${data.code} applied`);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const clearDiscount = () => setDiscount(null);
  const clearCart = () => {
    setItems([]);
    setDiscount(null);
  };

  const subtotal = useMemo(
    () => Math.round(items.reduce((s, i) => s + i.price * i.qty, 0) * 100) / 100,
    [items]
  );
  const discountAmount = useMemo(() => {
    if (!discount) return 0;
    if (discount.type === "percent")
      return Math.round(subtotal * discount.value) / 100;
    return Math.min(discount.value, subtotal);
  }, [discount, subtotal]);
  const total = Math.round((subtotal - discountAmount) * 100) / 100;
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        setQty,
        clearCart,
        discount,
        applyDiscount,
        clearDiscount,
        setDiscount,
        subtotal,
        discountAmount,
        total,
        count,
        drawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
